import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Alert,
  Skeleton,
  Stack,
  Tooltip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import {
  useTransitWarehouse,
  useResolveLeftover,
  useSetLeftoverNote,
  useWarehouses,
  type TransitWarehouseBooking,
  type TransitWarehousePartItem,
  type TransitWarehouseFilters,
} from '@/hooks/useInventory';
import { LeftoverKeepDialog, LeftoverNoteDialog } from '@/components/inventory';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useAuthStore } from '@/stores/authStore';
import { useNotify } from '@/stores/notificationStore';
import { ApiError } from '@/lib/api';
import { useOpenWorkOrder } from '@/hooks/useWorkOrderWindow';

const STALE_DAYS_THRESHOLD = 14;

const consumptionStatusColorMap: Record<string, 'default' | 'warning' | 'success' | 'info' | 'error'> = {
  pending: 'default',
  consumed: 'success',
  leftover_pending: 'warning',
  leftover_keep: 'info',
  leftover_return: 'info',
};

const consumptionStatusKeyMap: Record<string, string> = {
  pending: 'transitWarehouse.status.received',
  consumed: 'transitWarehouse.status.consumed',
  leftover_pending: 'transitWarehouse.status.leftoverPending',
  leftover_keep: 'transitWarehouse.status.leftoverKeep',
  leftover_return: 'transitWarehouse.status.leftoverReturn',
};

export default function TransitWarehousePage() {
  const { t } = useTranslation('inventory');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const openWorkOrder = useOpenWorkOrder();
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const canAdjust = hasPermission('inventory.adjust');

  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<'all' | 'unresolved' | 'stale'>('all');

  const filters: TransitWarehouseFilters = useMemo(() => ({
    ...(filter !== 'all' && { filter }),
    ...(searchQuery && { q: searchQuery }),
  }), [filter, searchQuery]);

  const { data, isLoading } = useTransitWarehouse(filters);
  const { data: warehousesData } = useWarehouses();
  const resolveLeftover = useResolveLeftover();
  const setNote = useSetLeftoverNote();

  const [keepTarget, setKeepTarget] = useState<TransitWarehousePartItem | null>(null);
  const [returnTarget, setReturnTarget] = useState<TransitWarehousePartItem | null>(null);
  const [noteTarget, setNoteTarget] = useState<TransitWarehousePartItem | null>(null);

  const bookings = data?.data ?? [];
  const summary = data?.summary ?? { totalBookings: 0, totalParts: 0, totalValueCents: 0, staleCount: 0 };
  const warehouses = warehousesData?.data ?? [];

  const insufficientStockMessage = (error: unknown): string | null => {
    if (error instanceof ApiError && error.data && typeof error.data === 'object' && 'message' in error.data) {
      const { message } = error.data as { message?: string };
      return typeof message === 'string' ? message : null;
    }
    return null;
  };

  const handleKeepConfirm = (warehouseId: string, binLocationId: string | null) => {
    if (!keepTarget?.workOrderPartId) return;
    resolveLeftover.mutate(
      {
        items: [{
          workOrderPartId: keepTarget.workOrderPartId,
          partId: keepTarget.partId,
          quantity: keepTarget.quantityRemaining,
          action: 'keep',
          warehouseId,
          ...(binLocationId ? { binLocationId } : {}),
        }],
      },
      {
        onSuccess: () => {
          notify.success(t('transitWarehouse.resolveLeftover.success'));
          setKeepTarget(null);
        },
        onError: (error) => {
          notify.error(tc('error'), insufficientStockMessage(error) ?? t('transitWarehouse.resolveLeftover.failed'));
        },
      },
    );
  };

  const handleReturnConfirm = () => {
    if (!returnTarget?.workOrderPartId) return;
    resolveLeftover.mutate(
      {
        items: [{
          workOrderPartId: returnTarget.workOrderPartId,
          partId: returnTarget.partId,
          quantity: returnTarget.quantityRemaining,
          action: 'return_to_supplier',
        }],
      },
      {
        onSuccess: (response) => {
          notify.success(
            response.returnNumber
              ? t('transitWarehouse.resolveLeftover.successWithReturn', { returnNumber: response.returnNumber })
              : t('transitWarehouse.resolveLeftover.success'),
          );
          setReturnTarget(null);
        },
        onError: (error) => {
          notify.error(tc('error'), insufficientStockMessage(error) ?? t('transitWarehouse.resolveLeftover.failed'));
        },
      },
    );
  };

  const handleSaveNote = (note: string | null) => {
    if (!noteTarget?.workOrderPartId) return;
    setNote.mutate(
      { workOrderPartId: noteTarget.workOrderPartId, note },
      {
        onSuccess: () => {
          notify.success(t('transitWarehouse.noteDialog.success'));
          setNoteTarget(null);
        },
        onError: () => notify.error(tc('error'), t('transitWarehouse.noteDialog.failed')),
      },
    );
  };

  const formatCents = (cents: number) => `${(cents / 100).toFixed(2)} €`;

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" fontWeight={600} gutterBottom>
          {t('transitWarehouse.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('transitWarehouse.description')}
        </Typography>
      </Box>

      {/* Summary cards */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        <Card sx={{ minWidth: 140, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">{t('transitWarehouse.totalWorkOrders')}</Typography>
            <Typography variant="h5" fontWeight={600}>{summary.totalBookings}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 140, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">{t('transitWarehouse.totalParts')}</Typography>
            <Typography variant="h5" fontWeight={600}>{summary.totalParts}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 140, flex: 1 }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">{t('transitWarehouse.totalValue')}</Typography>
            <Typography variant="h5" fontWeight={600}>{formatCents(summary.totalValueCents)}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 140, flex: 1, ...(summary.staleCount > 0 && { borderColor: 'warning.main', borderWidth: 2, borderStyle: 'solid' }) }}>
          <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">{t('transitWarehouse.staleItems')}</Typography>
            <Typography variant="h5" fontWeight={600} color={summary.staleCount > 0 ? 'warning.main' : 'text.primary'}>
              {summary.staleCount}
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Stale alert */}
      {summary.staleCount > 0 && (
        <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mb: 2 }}>
          {t('transitWarehouse.staleAlert', { count: summary.staleCount, days: STALE_DAYS_THRESHOLD })}
        </Alert>
      )}

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder={t('searchPlaceholder')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 280 }}
        />
        <ToggleButtonGroup
          value={filter}
          exclusive
          onChange={(_, v) => { if (v) setFilter(v); }}
          size="small"
        >
          <ToggleButton value="all">{t('transitWarehouse.filterAll')}</ToggleButton>
          <ToggleButton value="unresolved">{t('transitWarehouse.filterUnresolved')}</ToggleButton>
          <ToggleButton value="stale">{t('transitWarehouse.filterStale')}</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {/* Content */}
      {isLoading ? (
        <Stack spacing={2}>
          {[1, 2, 3].map((i) => <Skeleton key={i} variant="rounded" height={120} />)}
        </Stack>
      ) : bookings.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <WarehouseIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography color="text.secondary">{t('transitWarehouse.noItems')}</Typography>
        </Box>
      ) : (
        <Stack spacing={2}>
          {bookings.map((booking) => (
            <TransitBookingCard
              key={booking.bookingId}
              booking={booking}
              t={t}
              formatCents={formatCents}
              canAdjust={canAdjust}
              onKeep={setKeepTarget}
              onReturn={setReturnTarget}
              onEditNote={setNoteTarget}
              onNavigate={() => openWorkOrder(booking.bookingId)}
            />
          ))}
        </Stack>
      )}

      {keepTarget && (
        <LeftoverKeepDialog
          open={!!keepTarget}
          onClose={() => { setKeepTarget(null); }}
          partName={keepTarget.partName}
          quantity={keepTarget.quantityRemaining}
          warehouses={warehouses}
          onConfirm={handleKeepConfirm}
          isLoading={resolveLeftover.isPending}
        />
      )}

      <ConfirmDialog
        open={!!returnTarget}
        title={t('transitWarehouse.returnConfirm.title')}
        message={
          returnTarget
            ? t('transitWarehouse.returnConfirm.message', { count: returnTarget.quantityRemaining, partName: returnTarget.partName })
            : ''
        }
        confirmText={t('transitWarehouse.actions.returnToSupplier')}
        confirmColor="secondary"
        onConfirm={handleReturnConfirm}
        onCancel={() => { setReturnTarget(null); }}
        loading={resolveLeftover.isPending}
      />

      {noteTarget && (
        <LeftoverNoteDialog
          open={!!noteTarget}
          onClose={() => { setNoteTarget(null); }}
          partName={noteTarget.partName}
          initialNote={noteTarget.resolutionNote}
          onSave={handleSaveNote}
          isLoading={setNote.isPending}
        />
      )}
    </Box>
  );
}

function TransitBookingCard({
  booking,
  t,
  formatCents,
  canAdjust,
  onKeep,
  onReturn,
  onEditNote,
  onNavigate,
}: {
  booking: TransitWarehouseBooking;
  t: (key: string, opts?: Record<string, unknown>) => string;
  formatCents: (cents: number) => string;
  canAdjust: boolean;
  onKeep: (part: TransitWarehousePartItem) => void;
  onReturn: (part: TransitWarehousePartItem) => void;
  onEditNote: (part: TransitWarehousePartItem) => void;
  onNavigate: () => void;
}) {
  return (
    <Card>
      <CardContent>
        {/* Booking header */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tooltip title={t('transitWarehouse.actions.openWorkOrder')}>
              <IconButton
                size="small"
                onClick={onNavigate}
                data-testid="open-work-order-btn"
                aria-label={t('transitWarehouse.actions.openWorkOrder')}
              >
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Typography variant="body2" color="text.secondary">
              {booking.customerName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {booking.vehicleInfo}
            </Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {booking.oldestReceiptDays != null && booking.oldestReceiptDays >= STALE_DAYS_THRESHOLD && (
              <Chip
                size="small"
                color="warning"
                icon={<WarningAmberIcon />}
                label={t('transitWarehouse.daysSinceReceived') + ': ' + booking.oldestReceiptDays}
              />
            )}
            <Typography variant="body2" fontWeight={500}>{formatCents(booking.totalValueCents)}</Typography>
          </Stack>
        </Stack>

        {/* Parts table */}
        <TableContainer component={Paper} variant="outlined" sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('name')}</TableCell>
                <TableCell>{t('partNumber')}</TableCell>
                <TableCell align="right">{t('transitWarehouse.quantityOrdered')}</TableCell>
                <TableCell align="right">{t('transitWarehouse.quantityReceived')}</TableCell>
                <TableCell align="right">{t('transitWarehouse.quantityConsumed')}</TableCell>
                <TableCell align="right">{t('transitWarehouse.quantityRemaining')}</TableCell>
                <TableCell>{t('transitWarehouse.poNumber')}</TableCell>
                <TableCell>{t('stockStatus.label')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {booking.parts.map((part, idx) => (
                <TableRow key={`${part.partId}-${idx}`}>
                  <TableCell>
                    <Typography variant="body2">{part.partName}</Typography>
                    {part.resolutionNote && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {part.resolutionNote}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{part.partSku}</TableCell>
                  <TableCell align="right">{part.quantityOrdered}</TableCell>
                  <TableCell align="right">{part.quantityReceived}</TableCell>
                  <TableCell align="right">{part.quantityConsumed}</TableCell>
                  <TableCell align="right">{part.quantityRemaining}</TableCell>
                  <TableCell>{part.poOrderNumber}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={consumptionStatusColorMap[part.consumptionStatus] ?? 'default'}
                      label={t(consumptionStatusKeyMap[part.consumptionStatus] ?? 'transitWarehouse.status.received')}
                    />
                  </TableCell>
                  <TableCell>
                    {part.consumptionStatus === 'leftover_pending' && part.workOrderPartId && (
                      <Stack direction="row" spacing={0.5}>
                        {canAdjust && (
                          <>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<WarehouseIcon />}
                              onClick={() => { onKeep(part); }}
                              data-testid={`keep-in-stock-btn-${part.partId}`}
                            >
                              {t('transitWarehouse.actions.keepInWarehouse')}
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              color="secondary"
                              startIcon={<LocalShippingIcon />}
                              onClick={() => { onReturn(part); }}
                              data-testid={`return-to-supplier-btn-${part.partId}`}
                            >
                              {t('transitWarehouse.actions.returnToSupplier')}
                            </Button>
                          </>
                        )}
                        <Tooltip title={part.resolutionNote ? t('transitWarehouse.actions.editNote') : t('transitWarehouse.actions.addNote')}>
                          <IconButton
                            size="small"
                            onClick={() => { onEditNote(part); }}
                            data-testid={`leftover-note-btn-${part.partId}`}
                            aria-label={part.resolutionNote ? t('transitWarehouse.actions.editNote') : t('transitWarehouse.actions.addNote')}
                          >
                            <NoteAddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}
